import OpenSeadragon, { Rect, ControlAnchor } from '../../openseadragon';
import { h, render } from '../../preact';
import Annotations from './components/Annotations';
import AppStore from './store/Store';
import { createDispatcher } from './dispatcher/Dispatcher';
import controlClasses from './controls';
import initialize from './actions/initialize';
import selectMode from './actions/selectMode';
import cleanCanvas from './actions/cleanCanvas';
import fillCanvasWith from './actions/fillCanvasWith';
import zoom from './actions/zoom';
import rotate from './actions/rotate';

const viewerStates = new WeakMap();

function getState(viewer) {
  return viewerStates.get(viewer);
}

function ifPluginIsActive(fn) {
  return function checkIfActive(...args) {
    const state = getState(this);
    if (!state || !state.isPluginActive) {
      throw new Error('The OpenSeadragon Annotations plugin is not running');
    }
    return fn.apply(this, args);
  };
}

OpenSeadragon.Viewer.prototype.initializeAnnotations = function init(cb) {
  const viewer = this;

  let state = getState(viewer);
  if (state) {
    if (state.isPluginActive) {
      throw new Error('The OpenSeadragon Annotations plugin is already running on this viewer');
    }
    return;
  }

  const dispatcher = createDispatcher();
  const store = new AppStore(dispatcher);
  store.setLabelScale(viewer.annotationLabelScale || 1);
  const controls = controlClasses.map((ControlClass) => new ControlClass(store, dispatcher));

  state = {
    isPluginActive: false,
    openHandler: null,
    zoomHandler: null,
    rotationHandler: null,
    overlay: null,
    store,
    dispatcher,
    controls,
  };
  viewerStates.set(viewer, state);

  const updateZoom = (e) => zoom(e.zoom, dispatcher, store);
  const updateRotation = (e) => rotate(e.degrees ?? 0, dispatcher, store);

  const start = () => {
    state.zoomHandler = updateZoom;
    viewer.addHandler('zoom', updateZoom);

    state.rotationHandler = updateRotation;
    viewer.addHandler('rotate', updateRotation);

    const bounds = viewer.world.getHomeBounds();
    const rect = new Rect(0, 0, bounds.width, bounds.height);

    state.overlay = render(h(Annotations, { viewer, store, dispatcher }));
    viewer.addOverlay(state.overlay, rect);

    const currentZoom = viewer.viewport.getZoom();
    const currentRotation = viewer.viewport.getRotation?.() ?? 0;
    const boundingClientRect = state.overlay.getBoundingClientRect();
    initialize({
      zoom: currentZoom,
      width: boundingClientRect.width,
      height: boundingClientRect.height,
      rotation: currentRotation,
    }, dispatcher);

    controls.forEach((control) => {
      viewer.addControl(control.btn.element, {
        anchor: ControlAnchor.TOP_LEFT,
      });
    });

    if (state.openHandler) {
      viewer.removeHandler('open', state.openHandler);
      state.openHandler = null;
    }
    state.isPluginActive = true;
    if (cb) cb();
  };

  if (viewer.isOpen()) {
    start();
  } else {
    if (state.openHandler) {
      viewer.removeHandler('open', state.openHandler);
    }
    state.openHandler = start;
    viewer.addOnceHandler('open', start);
  }
};

// 라벨 글씨 배율 설정
OpenSeadragon.Viewer.prototype.setAnnotationLabelScale = function setLabelScale(labelScale) {
  this.annotationLabelScale = Math.max(1, Number(labelScale) || 1);
  const state = getState(this);
  if (state && state.store) state.store.setLabelScale(this.annotationLabelScale);
};

OpenSeadragon.Viewer.prototype.areAnnotationsActive = function areActive() {
  const state = getState(this);
  return !!(state && state.isPluginActive);
};

OpenSeadragon.Viewer.prototype.reattachAnnotationOverlay = function reattach() {
  const state = getState(this);
  if (!state || !state.isPluginActive || !state.overlay) return;

  const bounds = this.world.getHomeBounds();
  const rect = new Rect(0, 0, bounds.width, bounds.height);
  this.addOverlay(state.overlay, rect);

  const currentZoom = this.viewport.getZoom();
  const currentRotation = this.viewport.getRotation?.() ?? 0;
  const boundingClientRect = state.overlay.getBoundingClientRect();
  initialize({
    zoom: currentZoom,
    width: boundingClientRect.width,
    height: boundingClientRect.height,
    rotation: currentRotation,
  }, state.dispatcher);
};

OpenSeadragon.Viewer.prototype.startDrawing = ifPluginIsActive(function draw() {
  const { store, dispatcher } = getState(this);
  selectMode('DRAW', dispatcher, store);
});

OpenSeadragon.Viewer.prototype.startDrawRect = ifPluginIsActive(function drawRect() {
  const { store, dispatcher } = getState(this);
  selectMode('DRAWRECT', dispatcher, store);
});

OpenSeadragon.Viewer.prototype.startDrawEllipse = ifPluginIsActive(function drawEllipse() {
  const { store, dispatcher } = getState(this);
  selectMode('DRAWELLIPSE', dispatcher, store);
});

OpenSeadragon.Viewer.prototype.startDrawArrow = ifPluginIsActive(function drawArrow() {
  const { store, dispatcher } = getState(this);
  selectMode('DRAWARROW', dispatcher, store);
});

OpenSeadragon.Viewer.prototype.startRuler = ifPluginIsActive(function ruler() {
  const { store, dispatcher } = getState(this);
  selectMode('RULER', dispatcher, store);
});

const stopAnyDraw = function stopdraw() {
  const { store, dispatcher } = getState(this);
  selectMode('MOVE', dispatcher, store);
};

OpenSeadragon.Viewer.prototype.stopDrawing = ifPluginIsActive(stopAnyDraw);
OpenSeadragon.Viewer.prototype.stopDrawRect = ifPluginIsActive(stopAnyDraw);
OpenSeadragon.Viewer.prototype.stopDrawEllipse = ifPluginIsActive(stopAnyDraw);
OpenSeadragon.Viewer.prototype.stopDrawArrow = ifPluginIsActive(stopAnyDraw);
OpenSeadragon.Viewer.prototype.stopRuler = ifPluginIsActive(stopAnyDraw);

OpenSeadragon.Viewer.prototype.shutdownAnnotations = ifPluginIsActive(function shutdown() {
  const viewer = this;
  const state = getState(viewer);

  if (state.openHandler !== null) {
    throw new Error('An untriggered handler for the \'open\' event has been found');
  }
  if (state.overlay === null) {
    throw new Error('Null reference to the SVG overlay');
  }

  viewer.removeHandler('zoom', state.zoomHandler);
  state.zoomHandler = null;

  if (state.rotationHandler) {
    viewer.removeHandler('rotate', state.rotationHandler);
    state.rotationHandler = null;
  }

  viewer.removeOverlay(state.overlay);
  state.overlay = null;

  const ourControls = state.controls;
  const activeControls = viewer.controls;
  activeControls.forEach((viewportControl) => {
    ourControls.forEach((control) => {
      if (viewportControl.element === control.btn.element) {
        viewportControl.destroy();
      }
    });
  });

  ourControls.forEach((control) => {
    control.destroy?.();
  });

  selectMode('MOVE', state.dispatcher, state.store);
  cleanCanvas(state.dispatcher);

  state.store.destroy?.();
  state.isPluginActive = false;
  state.store = null;
  state.dispatcher = null;
  state.controls = [];
  viewerStates.delete(viewer);
});

export const get = (viewer) => {
  const state = getState(viewer);
  if (!state || !state.isPluginActive) {
    throw new Error('The OpenSeadragon Annotations plugin is not running on this viewer');
  }
  return state.store.getAll();
};

export const set = (viewer, annotations) => {
  const state = getState(viewer);
  if (!state || !state.isPluginActive) {
    throw new Error('The OpenSeadragon Annotations plugin is not running on this viewer');
  }
  fillCanvasWith(annotations, state.dispatcher);
};

export const clean = (viewer) => {
  const state = getState(viewer);
  if (!state || !state.isPluginActive) {
    throw new Error('The OpenSeadragon Annotations plugin is not running on this viewer');
  }
  cleanCanvas(state.dispatcher);
};

// 캡처용: 어노테이션이 그려진 SVG overlay 엘리먼트
export const getOverlay = (viewer) => {
  const state = getState(viewer);
  return state && state.isPluginActive ? state.overlay : null;
};

export const getStore = (viewer) => {
  const state = getState(viewer);
  return state ? state.store : null;
};
